import type { ReactNode } from 'react';
import type { ByMunicipalityStats, Tier } from '@auctionradar/shared';
import { useByMunicipality, useFunnel } from '../api/hooks';
import { TierBadge } from '../components/TierBadge';

const fmtMunicipality = (slug: string): string =>
  slug
    .replace(/^athens-/, 'Αθήνα · ')
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const pct = (n: number | null): string =>
  n == null ? '—' : `${Math.round(n * 100)}%`;

export function AnalyticsPage() {
  const funnel = useFunnel();
  const byMuni = useByMunicipality();

  const f = funnel.data;
  const muniRows: ByMunicipalityStats[] = byMuni.data?.items ?? [];

  // Funnel as chart bars — relative widths.
  const max = f ? Math.max(f.scraped, 1) : 1;
  const bar = (n: number, label: string, tone: string): ReactNode => (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="text-slate-500">{label}</span>
        <span className="font-medium text-slate-900">{n}</span>
      </div>
      <div className="mt-1 h-2 rounded bg-slate-100">
        <div
          className={`h-2 rounded ${tone}`}
          style={{ width: `${(n / max) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Analytics</h1>
        <p className="text-sm text-slate-500">
          Funnel + per-municipality breakdown.
        </p>
      </div>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Funnel</h2>
        {funnel.isLoading && (
          <div className="h-32 animate-pulse rounded-lg bg-white" />
        )}
        {f && (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
            {bar(f.scraped, 'Scraped', 'bg-slate-700')}
            {bar(f.analyzed, 'Analyzed', 'bg-slate-600')}
            {bar(f.scored, 'Scored', 'bg-amber-500')}
            {bar(f.alerted, 'Alerted', 'bg-rose-600')}
            <div className="border-t border-slate-100 pt-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Dismissed breakdown
              </h3>
              <dl className="mt-2 grid grid-cols-3 gap-3 text-sm">
                <Stat label="Wrong ownership" value={f.dismissed.notFullOwnership} />
                <Stat label="Red risk" value={f.dismissed.redRisk} />
                <Stat label="Other" value={f.dismissed.other} />
              </dl>
            </div>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-slate-700">
          Top municipalities
        </h2>
        {byMuni.isLoading && (
          <div className="h-32 animate-pulse rounded-lg bg-white" />
        )}
        {muniRows.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <Th>Δήμος</Th>
                  <Th>Περιοχή</Th>
                  <Th align="right">Leads</Th>
                  <Th align="right">Avg έκπτωση</Th>
                  <Th align="right">Best tier</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {muniRows.slice(0, 30).map((r) => (
                  <tr key={`${r.region}|${r.municipality}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      {fmtMunicipality(r.municipality)}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {r.region === 'corinth' ? 'Κορινθία' : 'Αττική'}
                    </td>
                    <td className="px-3 py-2 text-right">{r.leadCount}</td>
                    <td className="px-3 py-2 text-right">{pct(r.avgDiscount)}</td>
                    <td className="px-3 py-2 text-right">
                      <TierBadge tier={r.bestTier as Tier | null} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="text-base font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}
